import { useCallback, useEffect, useMemo, useState } from "react";
import { ExpensePlanner } from "./components/ExpensePlanner.tsx";
import { PrintReport } from "./components/PrintReport.tsx";
import { Summary } from "./components/Summary.tsx";
import { Feedback } from "./components/Feedback.tsx";
import { OtherSavingsFields } from "./components/OtherSavingsFields.tsx";
import { loadOtherSavings, saveOtherSavings, totalOtherSavings } from "./lib/otherSavings.ts";
import type { OtherSavings } from "./lib/otherSavings.ts";
import type { Tab } from "./components/Summary.tsx";
import { Inputs } from "./components/Inputs.tsx";
import { RepatriationPlanner } from "./components/RepatriationPlanner.tsx";
import { Results } from "./components/Results.tsx";
import { SpendChart } from "./components/SpendChart.tsx";
import { ThemeToggle } from "./components/ThemeToggle.tsx";
import { track } from "./lib/analytics.ts";
import { isExampleRepat, loadRepat, saveRepat } from "./lib/repatStore.ts";
import { runRepatriation } from "./model/repatriation.ts";
import { isStandardPlan, loadExpensePlan, saveExpensePlan } from "./lib/expenseStore.ts";
import { DEFAULT_PLAN, summarise } from "./model/expenses.ts";
import type { ExpensePlan } from "./model/expenses.ts";
import { cityById } from "./model/cities.ts";
import { usesSpendingPlan } from "./lib/spendingSource.ts";
import type { RepatriationInputs } from "./model/repatriation.ts";
import { usePlan } from "./lib/usePlan.ts";
import { applyCity, taxDragForSpend } from "./model/defaults.ts";
import { sanitize } from "./model/limits.ts";
import { readFromLocation, shareUrl, writeToLocation } from "./lib/urlState.ts";
import type { PlanInputs } from "./model/types.ts";

const THIS_YEAR = new Date().getFullYear();

export default function App() {
  // sanitize() is the single gate every plan passes through: typed edits, preset
  // changes, and anything decoded from a shared link. Nothing reaches the model
  // without being forced into a range it can answer.
  const [inputs, setInputs] = useState<PlanInputs>(() => sanitize(readFromLocation()).inputs);
  const [notices, setNotices] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<"calculator" | "expenses" | "repatriation">("calculator");

  const updateInputs = useCallback((next: PlanInputs) => {
    const checked = sanitize(next);
    setInputs(checked.inputs);
    setNotices(checked.notices);
  }, []);
  const { result, busy, error } = usePlan(inputs);

  // US savings, entered on the USD to INR tab, kept in this browser and shared
  // with the calculator so the corpus can be compared with what you have.
  const [repat, setRepat] = useState<RepatriationInputs>(loadRepat);
  useEffect(() => saveRepat(repat), [repat]);
  const repatResult = useMemo(() => runRepatriation(repat), [repat]);
  // Savings outside the US accounts: today's value, before tax, browser only.
  const [otherSavings, setOtherSavings] = useState<OtherSavings>(loadOtherSavings);
  useEffect(() => saveOtherSavings(otherSavings), [otherSavings]);
  const other = useMemo(
    () => totalOtherSavings(otherSavings, inputs.household.adultAge),
    [otherSavings, inputs.household.adultAge],
  );

  const savings = useMemo(
    () => ({
      grossInr: repatResult.grossUsd * repat.fxRate,
      grossUsd: repatResult.grossUsd,
      netInr: repatResult.netInr,
      fxRate: repat.fxRate,
      saleYear: repat.saleYear,
      isExample: isExampleRepat(repat),
      otherCounted: other.counted,
      npsExcluded: other.npsCounted ? 0 : other.nps,
    }),
    [repat, repatResult, other],
  );

  // The spending planner's figures, also kept in this browser and lifted here so
  // the landing summary and the print view can show them.
  const [expensePlan, setExpensePlan] = useState<ExpensePlan>(() =>
    loadExpensePlan({
      ...DEFAULT_PLAN,
      cityId: inputs.spending.cityId,
      children: Math.min(4, Math.max(0, inputs.household.childAges.length)),
    }),
  );
  useEffect(() => saveExpensePlan(expensePlan), [expensePlan]);
  const expenseSummary = useMemo(() => summarise(expensePlan), [expensePlan]);
  const retireYear = THIS_YEAR + inputs.yearsToRetirement;
  const planInUse = usesSpendingPlan(inputs.spending, {
    annualTotal: expenseSummary.annualTotal,
    cityId: expensePlan.cityId,
  });

  const printSummary = useCallback(() => {
    track("summary-printed");
    window.print();
  }, []);

  const openTab = useCallback((next: Tab) => {
    setView(next);
    track("tab-opened", { tab: next });
  }, []);

  // Count the first result of a visit only: results recompute on every edit,
  // and one event per edit would use up the free plan's monthly budget.
  const [resultCounted, setResultCounted] = useState(false);
  useEffect(() => {
    if (result && !resultCounted) {
      track("results-shown");
      setResultCounted(true);
    }
  }, [result, resultCounted]);

  useEffect(() => {
    writeToLocation(inputs);
  }, [inputs]);

  // The planner prices every line for the chosen city, so the figure it hands
  // over is marked as locally priced and the calculator does not discount it
  // for the city a second time.
  const applyExpenses = useCallback(
    (annualTotal: number, cityId: string, stay = false) => {
      updateInputs({
        ...inputs,
        spending: {
          ...applyCity(inputs.spending, cityId),
          lifestylePerYear: annualTotal,
          lifestylePricedLocally: true,
        },
        taxDrag: taxDragForSpend(annualTotal),
      });
      track("spending-applied");
      if (stay) return;
      setView("calculator");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [inputs, updateInputs],
  );

  // "Use plan" from the summary card or the slider: same as the planner's own
  // button, but leaves the person where they are.
  const switchToPlan = useCallback(
    () => applyExpenses(Math.round(expenseSummary.annualTotal), expensePlan.cityId, true),
    [applyExpenses, expenseSummary.annualTotal, expensePlan.cityId],
  );

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl(inputs));
      setCopied(true);
      track("link-copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the URL bar already holds the scenario.
      setCopied(false);
    }
  }, [inputs]);

  return (
    <>
      <div className="screen-only">
      <header className="masthead">
        <div className="masthead-top">
          <p className="eyebrow">Enoughwise · FIRE calculator for India</p>
          <ThemeToggle className="theme-in-head" />
        </div>
        <h1>What retiring in India actually costs</h1>
        <p className="dek">
          Most calculators apply one inflation rate to one lump of spending and stop at 30 years.
          This one inflates schooling, medical care and overseas tuition separately, charges tax on
          every withdrawal, and runs to 50 years — because those are the assumptions that move the
          answer by crores.
        </p>
        <p className="privacy">
          Your numbers never leave your browser. No account, no cookies. We count anonymous visits
          and which features get used, never the figures you enter.{" "}
          <a href="#privacy">Privacy note</a>
        </p>
      </header>

      <Summary
        data={{
          corpus: result ? result.corpus["0.90"] : null,
          yearOneGross: result ? result.yearOneGross : null,
          spending: {
            annualTotal: expenseSummary.annualTotal,
            standard: isStandardPlan(expensePlan),
            cityName: cityById(expensePlan.cityId).name,
          },
          lifestyleUsed: inputs.spending.lifestylePerYear,
          planInUse,
          savings,
        }}
        onOpen={(tab) => {
          openTab(tab);
          document.querySelector(".topbar")?.scrollIntoView({ behavior: "smooth" });
        }}
        onPrint={printSummary}
        onUsePlan={switchToPlan}
      />

      <div className="topbar">
      <nav className="tabs" aria-label="Sections">
        <button
          type="button"
          className={view === "calculator" ? "on" : undefined}
          aria-current={view === "calculator" ? "page" : undefined}
          onClick={() => openTab("calculator")}
        >
          <span className="tab-long">Corpus calculator</span>
          <span className="tab-short">Corpus</span>
        </button>
        <button
          type="button"
          className={view === "expenses" ? "on" : undefined}
          aria-current={view === "expenses" ? "page" : undefined}
          onClick={() => openTab("expenses")}
        >
          <span className="tab-long">Plan your spending</span>
          <span className="tab-short">Spending</span>
        </button>
        <button
          type="button"
          className={view === "repatriation" ? "on" : undefined}
          aria-current={view === "repatriation" ? "page" : undefined}
          onClick={() => openTab("repatriation")}
        >
          <span className="tab-long">USD → INR transfer</span>
          <span className="tab-short">USD → INR</span>
        </button>
      </nav>
        <button type="button" className="print-btn" onClick={printSummary} title="Print or save as PDF">
          Print
        </button>
        <ThemeToggle className="theme-in-bar" />
      </div>

      <main>
        {view === "repatriation" ? (
          <RepatriationPlanner inputs={repat} onChange={setRepat} />
        ) : view === "expenses" ? (
          <ExpensePlanner
            plan={expensePlan}
            setPlan={setExpensePlan}
            onApply={applyExpenses}
            inUse={planInUse}
            calculatorAmount={inputs.spending.lifestylePerYear}
          />
        ) : (
        <div className="layout">
          <aside>
            <Inputs
              inputs={inputs}
              onChange={updateInputs}
              notices={notices}
              spendingPlan={{
                inUse: planInUse,
                annualTotal: expenseSummary.annualTotal,
                cityName: cityById(expensePlan.cityId).name,
                onUse: switchToPlan,
                onOpen: () => openTab("expenses"),
              }}
            >
              <OtherSavingsFields
                value={otherSavings}
                onChange={setOtherSavings}
                ageAtRetirement={inputs.household.adultAge}
              />
            </Inputs>
          </aside>

          <div className="output">
            {error ? (
              <div className="error" role="alert">
                <strong>The calculation failed.</strong> {error}
              </div>
            ) : result ? (
              <>
                <Results
                  result={result}
                  inputs={inputs}
                  busy={busy}
                  savings={savings}
                  retireYear={retireYear}
                  onEditSavings={() => {
                    openTab("repatriation");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
                <SpendChart result={result} startYear={THIS_YEAR + inputs.yearsToRetirement} />
              </>
            ) : (
              <div className="loading">Running the simulation…</div>
            )}

            <div className="actions">
              <button type="button" onClick={copyLink}>
                {copied ? "Link copied" : "Copy link to this scenario"}
              </button>
            </div>
          </div>
        </div>
        )}

        <Feedback />

        <footer>
          <h2>What this is not</h2>
          <p>
            It is a calculator, not advice. Every number it produces is an output of assumptions you
            can see and change in the panel on the left — change them and the answer changes. It
            does not know your tax position, your health, or your family. It cannot tell you whether
            to retire.
          </p>
          <p>
            Every assumption is shown beside the input it sets, with its source, and you can change
            any of them. If you think one is wrong, email{" "}
            <a href="mailto:feedback@enoughwise.com">feedback@enoughwise.com</a> with the number you would
            use and why.
          </p>

          <section id="privacy" className="privacy-note" aria-labelledby="privacy-title">
            <h2 id="privacy-title">Privacy</h2>
            <p>
              <strong>What you enter stays on your device.</strong> The calculator runs in your
              browser; there is no server-side calculation and no account. Your plan is kept in the
              page address after the # sign, which browsers do not send to any server. Your spending
              plan, other savings and US figures are kept in this browser's storage and are never
              put in a share link. Your light or dark choice is kept there too.
            </p>
            <p>
              <strong>Share links.</strong> A link you copy contains your corpus plan. Anyone you
              send it to can see those figures, so share it only with people you'd show them to.
            </p>
            <p>
              <strong>Anonymous usage counts.</strong> We use{" "}
              <a href="https://umami.is" rel="noopener noreferrer">Umami</a>, a cookieless analytics
              service, to count page visits, the referring site, browser, operating system, device
              type and approximate location, and which features are used (for example, a result
              shown or a summary printed). It never receives an amount, an age, a city or any other
              figure you enter.
            </p>
            <p>
              <strong>Feedback.</strong> The Yes / No vote is counted the same anonymous way. If you
              email <a href="mailto:feedback@enoughwise.com">feedback@enoughwise.com</a>, we see your email
              address and message, and use them only to read and reply to your feedback.
            </p>
            <p>
              <strong>Hosting.</strong> The site is served by Cloudflare, which handles standard
              connection data, such as your IP address, to deliver and protect it. No cookies,
              advertising or tracking across other sites.
            </p>
            <p>
              Questions about privacy:{" "}
              <a href="mailto:feedback@enoughwise.com">feedback@enoughwise.com</a>.
            </p>
          </section>
        </footer>
      </main>
      </div>

      <PrintReport
        inputs={inputs}
        result={result}
        retireYear={retireYear}
        plan={expensePlan}
        expenses={expenseSummary}
        repat={repat}
        repatResult={repatResult}
        repatIsExample={savings.isExample}
        otherSavings={otherSavings}
        otherCounted={other.counted}
        npsCounted={other.npsCounted}
        planInUse={planInUse}
      />
    </>
  );
}
